import { StateCreator } from 'zustand';
import {
    AppState,
    ClientDataState,
    DEFAULT_TEAM_BONUS,
    TeamBonusConfig,
    TeamBonusMode,
} from '../types';
import { saveToLocalStorage } from '../utils';
import { normalizeTeamBonus } from '../selectors/teamBonusSelectors';

type TeamBonusSliceType = Pick<
    AppState,
    | 'setTeamBonusMode'
    | 'setTeamBonusPoolAmount'
    | 'toggleTeamBonusMember'
    | 'setTeamBonusSelectedIds'
    | 'applyTeamBonusShares'
>;

const getConfig = (clientData: ClientDataState): TeamBonusConfig =>
    normalizeTeamBonus(clientData.teamBonus || DEFAULT_TEAM_BONUS);

/**
 * Speichert NUR teamBonus-Konfiguration (Modus, Pool-Vorschau, Auswahl).
 * Schreibt NIEMALS in ma.bonus / Personalarrays — das würde die laufende
 * Halbjahres-Kalkulation (Lohnfaktor etc.) verfälschen. Auszahlung = manuell
 * durch Admin/Coach NACH dem Halbjahr in den Personaldaten.
 */
const commitTeamBonusConfig = (
    set: (fn: (state: AppState) => Partial<AppState>) => void,
    nextClientData: ClientDataState
) => {
    set((state) => {
        const patched: ClientDataState = {
            ...nextClientData,
            teamBonus: getConfig(nextClientData),
        };
        const nextState = { ...state, clientData: patched, isDirty: true };
        saveToLocalStorage(nextState);
        return { clientData: patched, isDirty: true };
    });
};

export const createTeamBonusSlice: StateCreator<AppState, [], [], TeamBonusSliceType> = (set, get) => ({
    setTeamBonusMode: (mode: TeamBonusMode) => {
        const state = get();
        const prev = getConfig(state.clientData);
        const next: ClientDataState = {
            ...state.clientData,
            teamBonus: { ...prev, bonusMode: mode },
        };
        commitTeamBonusConfig(set, next);
    },

    setTeamBonusPoolAmount: (amount: number) => {
        const state = get();
        const prev = getConfig(state.clientData);
        const next: ClientDataState = {
            ...state.clientData,
            teamBonus: { ...prev, poolAmount: Math.max(0, Number(amount) || 0) },
        };
        commitTeamBonusConfig(set, next);
    },

    toggleTeamBonusMember: (memberId: string) => {
        const state = get();
        const prev = getConfig(state.clientData);
        const setIds = new Set(prev.selectedMemberIds);
        if (setIds.has(memberId)) setIds.delete(memberId);
        else setIds.add(memberId);
        const next: ClientDataState = {
            ...state.clientData,
            teamBonus: { ...prev, selectedMemberIds: Array.from(setIds) },
        };
        commitTeamBonusConfig(set, next);
    },

    setTeamBonusSelectedIds: (ids: string[]) => {
        const state = get();
        const prev = getConfig(state.clientData);
        const next: ClientDataState = {
            ...state.clientData,
            teamBonus: {
                ...prev,
                selectedMemberIds: ids.filter((id) => typeof id === 'string' && id.length > 0),
            },
        };
        commitTeamBonusConfig(set, next);
    },

    /** Nur Config persistieren — kein Write in Personal-Boni. */
    applyTeamBonusShares: () => {
        const state = get();
        commitTeamBonusConfig(set, state.clientData);
    },
});
